import _ from 'lodash';
import Date from './Date';
import jQuery from 'jquery';
import OddsFormatProvider from './OddsFormatProvider';

/**
 * Reference to the container element
 *
 * @type    {HTMLElement}
 */
var container = null;

/**
 * Placeholder for the passed options
 *
 * @type    {Object}
 */
var options = {};

/**
 * Sets the container
 *
 * @param   {String}  localContainer
 *
 * @returns {void}
 */
var setContainer = function setContainer(localContainer) {

    container = localContainer;
};

/**
 * Sets the options
 *
 * @param   {Object}  localOptions
 *
 * @returns {void}
 */
var setOptions = function setOptions(localOptions) {

    options = localOptions;
};

/**
 * Finds all children which match the given selector
 *
 * @param parent
 * @param selector
 * @returns {*}
 */
var getChildElementsBySelector = function getChildElementsBySelector(parent, selector) {

    if (!selector) {
        return [];
    }

    return parent.querySelectorAll(selector);
};

/**
 * Get the last element matching a given selector
 *
 * @param   {HTMLElement}   parent
 * @param   {String}        selector
 *
 * @returns {HTMLElement}
 */
var getLastChild = function getLastChild(parent, selector) {

    var elems = getChildElementsBySelector(parent, selector + ':last-of-type');

    return elems[elems.length - 1];
};

/**
 * Checks if a given node is a group or sub group
 *
 * @param   {Object}    node    node to process
 *
 * @returns {boolean}
 */
var isGroupOrSubGroup = function isGroupOrSubGroup(node) {

    return node.isNewGroup || node.isNewSubGroup;
};

/**
 * Html string to element
 *
 * @param html
 * @returns {DocumentFragment}
 */
var createElement = function createElement(html) {

    var div = document.createElement('div'),
        fragment = document.createDocumentFragment();

    div.innerHTML = html;

    /**
     * adds element to fragment, while avoiding the
     * use of 'bind'
     *
     * @param {Object} element DOM element
     *
     * @returns {void}
     *
     * @private
     */
    var addToFragment = function (element) {

        addElementToFragment(fragment, element);
    };

    _.each(div.children, addToFragment);

    parseRows(fragment);

    return fragment;
};

/**
 * Appends an element to a container
 *
 * @param   {HTMLElement}   container
 * @param   {HTMLElement}   element
 *
 * @returns {HTMLElement}
 */
var appendToDom = function appendToDom(container, element) {

    return container.appendChild(element);
};

/**
 * Adds an element to a given fragment
 *
 * @param fragment
 * @param node
 */
var addElementToFragment = function addElementToFragment(fragment, node) {

    fragment.appendChild(node.cloneNode(true));
};

/**
 * Defaults the new group container to the one stored in the module scope if not provided otherwise
 *
 * @param {Object} localContainer The parent container where we should append the new group
 *
 * @returns {Object} The container for the new group
 *
 * @private
 */
var getNewGroupContainer = function getNewGroupContainer(localContainer) {

    if (localContainer) {

        return localContainer;
    } else {

        return container;
    }
};

/**
 * Adds a group to the dom
 *
 * @param {Object} node containing information that indicates if we have to create a new group
 * @param {Object} fragment The fragment to append to the dom
 * @param {Object} container The parent container where we should append the new group
 *
 * @returns {void}
 *
 * @private
 */
var addGroup = function addGroup(node, fragment, container) {

    if (node.isNewGroup) {

        // append to container
        appendToDom(getNewGroupContainer(container), fragment.cloneNode(true));
    }
};

/**
 * Add sub group to dom
 *
 * @param element
 */
var addSubGroup = function addSubGroup(node, fragment) {

    if (node.isNewSubGroup) {

        var lastGroup = getLastChild(container, options.group);

        // append to last group
        appendToDom(lastGroup, fragment.cloneNode(true));
    }
};

/**
 * Add rows to the dom and process them
 *
 * @param fragment
 */
var addRows = function addRows(fragment) {

    var lastSubGroup = getLastChild(container, options.subGroup);

    // append to last group
    appendToDom(lastSubGroup, fragment.cloneNode(true));
};

/**
 * Parsed the rows added for dates and active selections
 *
 * @param fragment
 */
var parseRows = function parseRows(fragment) {

    parseSelectionsInFragment(fragment);
    parseDatesInFragment(fragment);
};

/**
 * Check if a selection is added to the betslip
 *
 * @param   {DocumentFragment}  fragment
 */
var parseSelectionsInFragment = function parseSelectionsInFragment(fragment) {

    var selections = fragment.querySelectorAll(options.selection);

    _.each(selections, parseSelections);
};

/**
 * Handles all selection elements related functionality making sure all required selection
 * dom operations are completed before inserting the element into the DOM
 *
 * @param {HTMLElement} selectionElem
 *
 * @returns {void}
 */
var parseSelections = function parseSelections(selectionElem) {

    parseOdds(selectionElem);
    isInBetSlip(selectionElem);
};

/**
 * Queries the parent fragment for all the selection odds elements
 *
 * @param {HTMLElement} parent
 */
var parseOdds = function parseOdds(parent) {

    var odds = parent.querySelectorAll(options.selectionOdds);

    _.each(odds, parseOdd);
};

/**
 * Parse the odds to the user odd format
 *
 * @param oddElem
 */
var parseOdd = function parseOdd(oddElem) {

    var odd = oddElem.getAttribute('data-odds');

    if (odd) {

        oddElem.setAttribute('data-odds', OddsFormatProvider.convertSingle(odd));
    } else {

        // New templates have the odd as proter html instead of data attr
        oddElem.innerHTML = OddsFormatProvider.convertSingle(oddElem.innerHTML);
    }
};

/**
 * Checks if selection is in betslip and appends the required classes if so
 *
 * @param element
 */
var isInBetSlip = function isInBetSlip(element) {

    var isInSlip = options.highlightedSelections.indexOf(element.id);

    if (isInSlip !== -1) {

        element.classList.add('active', '-selectionadded');
    }
};

/**
 * Check if a selection is added to the betslip
 *
 * @param   {DocumentFragment}  fragment
 */
var parseDatesInFragment = function parseDatesInFragment(fragment) {

    if (!options.timeSelector) {

        return;
    }

    var timeElems = fragment.querySelectorAll(options.timeSelector);

    _.each(timeElems, parseDateElement);
};

/**
 * Parses the dates on each row
 *
 * @param element
 */
var parseDateElement = function parseDateElement(element) {

    var startDate = element.getAttribute('datetime');
    element.innerHTML = Date.format(startDate, 'DD MMM HH:mm');
};

/**
 * Destroys the dom bindings
 *
 * @returns {void}
 */
var destroy = function destroy() {

    container = null;
    options = {};
};

/**
 * Returns position value
 *
 * @param  {Number} position Position to inset the element on
 *
 * @return {Number}          Position value
 *
 * @private
 */
var getPositionValue = function getPositionValue(position) {

    return position === 0 ? position : position - 1;
};

/**
 * Function called after the first group was added to the parent container.
 *
 * @param {Function} firstGroupAddedCallback initialization code in the situation of an empty parent container.
 *
 * @returns {void}
 *
 * @private
 */
var runFirstGroupAddedCallback = function runFirstGroupAddedCallback(firstGroupAddedCallback) {

    if (_.isFunction(firstGroupAddedCallback)) {

        firstGroupAddedCallback();
    }
};

/**
 * Get the previous element
 *
 * @param {Object} rows jQuery Page rows
 * @param {number} position to inset the element on
 *
 * @returns {Object} previous jQuery element
 *
 * @private
 */
var getPreviousElement = function getPreviousElement(rows, position) {

    if (rows.length < position) {

        //just return the last row from the non empty container
        return rows.eq(rows.length - 1);
    } else {

        return rows.eq(position - 1);
    }
};

/**
 * Function called to insert a group in a container that is not empty.
 *
 * @param {Object} fragment to be inserted into the page
 * @param {Object} rows jQuery Page rows
 * @param {number} position to inset the element on
 *
 * @returns {void}
 *
 * @private
 */
var insertInNonEmptyContainer = function insertInNonEmptyContainer(fragment, rows, position) {

    if (position === 0) {

        jQuery(fragment).insertBefore(rows.eq(0));
    } else {

        jQuery(fragment).insertAfter(getPreviousElement(rows, position));
    }
};

/**
 * Public functions
 *
 * @type {Object}
 */
var api = {

    /**
     * Sets the required values
     *
     * @param   {HTMLElement}   container
     * @param   {Object}        selectors
     */
    init: function init(container, options) {

        setContainer(container);
        setOptions(options);
    },

    /**
     * Adds a batch node to the page
     *
     * @param node
     * @param {Object} container The container where to add the node
     *
     * @return {DocumentFragment} The created document fragment
     */
    addNode: function addNode(node, container) {

        var fragment = createElement(node.html);

        if (!isGroupOrSubGroup(node, fragment)) {

            addRows(fragment);
        }
        else {

            addGroup(node, fragment.childNodes[0], container);
            addSubGroup(node, fragment.childNodes[0]);
        }

        return fragment;
    },

    /**
     * Adds a row at a given position in the dom.
     *
     * @param {Object} node to be inserted into the page. Expects key and html props
     * @param {number} position to inset the element on
     * @param {Object} container The container where to insert the row
     * @param {String} rowSelector A selector used to identify what is considered a row
     *
     * @returns {void}
     *
     * @public
     */
    addRowInContainer: function addRowInContainer(node, position, container, rowSelector) {

        var rows = getChildElementsBySelector(
            container,
            rowSelector
        );
        var row = rows[getPositionValue(position)];

        if (!row) {

            return false;
        }

        var fragment = createElement(node.template);

        // insert before
        row.parentNode.insertBefore(fragment, row);
    },

    /**
     * Adds a row at a given position in the dom.
     *
     * @param {Object} node     node to be inserted into the page. Expects key and html props
     * @param {number} position Position to inset the element on
     *
     * @returns {void}
     */
    addRowAtPosition: function addRowAtPosition(node, position) {

        api.addRowInContainer(node, position, container, options.row);
    },

    /**
     * Appends a row to a container dom.
     *
     * @param {String} html to be appended to the parent.
     * @param {Object} container The container where to insert the row
     *
     * @returns {void}
     *
     * @public
     */
    appendRow: function appendRow(html, container) {

        var fragment = createElement(html);
        appendToDom(container, fragment);
    },

    /**
     * Adds a row at a given position in the dom.
     *
     * @param {Object} html to be inserted into the page.
     * @param {number} position to inset the element on
     * @param {Object} selectors Containing selectors to identify the position in the context of the group parent
     *     @param {String} rowSelector A selector used to identify what is considered a row
     *     @param {String} parentSelector A selector used to find the parent where to insert the row
     * @param {Function} firstGroupAddedCallback initialization code in the situation of an empty parent container.
     *
     * @returns {void}
     *
     * @public
     */
    addGroupAtPosition: function addGroupAtPosition(html, position, selectors, firstGroupAddedCallback) {

        var fragment = createElement(html);

        var rows = jQuery(selectors.rowSelector);

        if (rows.length === 0) {

            jQuery(selectors.parentSelector).append(jQuery(fragment));
            runFirstGroupAddedCallback(firstGroupAddedCallback);
        } else {

            insertInNonEmptyContainer(fragment, rows, position);
        }
    },

    /**
     * Html string to element
     *
     * @param html
     *
     * @returns {DocumentFragment}
     *
     * @public
     */
    createElement: createElement,

    destroy: destroy,
};

export default api;
